import { AnyAri } from '@atlassian/ari/any-ari';

/**
 * Metadata about a TWG node type, keyed by ATI string.
 */
export type AtiNodeMetadata = {
	/** Canonical TWG node name (e.g. "ThirdPartyUser", "ConfluencePage") — used for grouping and lookups */
	typename: string;
	/** Human-readable display name (e.g. "Third Party User", "Confluence Page") */
	displayName: string;
};

/**
 * Attempts to resolve node metadata from an ARI string by parsing it and
 * looking up the ATI in the provided map.
 *
 * @returns The resolved metadata, or null if ARI parsing fails or the ATI
 *          is not found in the map.
 */
export function resolveNodeMetadataFromAri(
	ari: string,
	atiNodeMap: Record<string, AtiNodeMetadata>,
): AtiNodeMetadata | null {
	try {
		const parsed = AnyAri.parse(ari);
		const atiString = parsed.ati.toString();

		const metadata = atiNodeMap[atiString];
		if (metadata) {
			return metadata;
		}

		// No exact match in the schema — only derive from the resource type
		// if the map is populated (non-empty). When the schema hasn't loaded
		// yet (empty map), we want to fall through so downstream code can
		// use __typename or other fallbacks.
		if (Object.keys(atiNodeMap).length > 0) {
			const resourceType = parsed.resourceType;
			if (resourceType) {
				return {
					typename: pascalCaseResourceType(resourceType),
					displayName: humanizeResourceType(resourceType),
				};
			}
		}
	} catch {
		// ARI parsing failed — fall through
	}
	return null;
}

/**
 * Converts a hyphenated resource type string to a human-readable display
 * name by capitalising each segment.
 *
 * e.g. "third-party-user" → "Third Party User"
 */
function humanizeResourceType(resourceType: string): string {
	return resourceType
		.split('-')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

/**
 * Converts a hyphenated resource type string to a PascalCase typename
 * suitable for grouping and lookups.
 *
 * e.g. "third-party-user" → "ThirdPartyUser"
 */
function pascalCaseResourceType(resourceType: string): string {
	return resourceType
		.split('-')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join('');
}

/**
 * Whether a string is a well-formed ARI with a resource type (e.g. rejects `ari:cloud:jira::`).
 */
export const isValidAri = (value: string): boolean => {
	try {
		const parsed = AnyAri.parse(value);
		return parsed.resourceType !== undefined && parsed.resourceType !== '';
	} catch {
		return false;
	}
};

/**
 * Returns the ATI string for an ARI, or null if parsing fails.
 *
 * e.g. `ari:third-party:google.google-drive::document/id`
 *   → `ati:third-party:google.google-drive:document`
 */
export function atiStringFromAri(ari: string): string | null {
	try {
		return AnyAri.parse(ari).ati.toString();
	} catch {
		return null;
	}
}

/**
 * Given a list of ARIs, resolves the unique TWG node type names
 * (the keys in `nodesMap`) needed to hydrate them.
 *
 * Chain: ARI → AnyAri.parse() → ATI string → atiNodeMap → typename
 *
 * These typenames can then be passed to `convertLabelsToGraphQLTypes()`
 * which will look them up in `nodesMap` and extract the appropriate
 * GraphQL hydration types.
 *
 * @returns Array of unique node type names (e.g. ['ConfluenceBlogpost', 'JiraIssue'])
 */
export function resolveNodeTypenamesFromAris(
	aris: string[],
	atiNodeMap: Record<string, AtiNodeMetadata>,
): string[] {
	const typeNames = new Set<string>();

	for (const ari of aris) {
		const metadata = resolveNodeMetadataFromAri(ari, atiNodeMap);
		if (metadata) {
			typeNames.add(metadata.typename);
		}
	}

	return Array.from(typeNames);
}
